import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import { SpinnerGraphic } from "./internals/SpinnerGraphic";

export type SpinnerSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
export type SpinnerTone = "default" | "secondary" | "inverse";
export type SpinnerReveal = "immediate" | "delayed";

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  "2xs": "size-spinner-2xs",
  xs: "size-spinner-xs",
  sm: "size-spinner-sm",
  md: "size-spinner-md",
  lg: "size-spinner-lg",
  xl: "size-spinner-xl",
  "2xl": "size-spinner-2xl",
  "3xl": "size-spinner-3xl",
};

const TONE_CLASSES: Record<SpinnerTone, string> = {
  default: "text-text-tertiary",
  secondary: "text-text-secondary",
  inverse: "text-text-default-inverse",
};

export function Spinner({
  size = "md",
  tone = "default",
  reveal = "immediate",
}: {
  size?: SpinnerSize;
  tone?: SpinnerTone;
  reveal?: SpinnerReveal;
}): ReactNode {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      className={twJoin(
        "shrink-0",
        SIZE_CLASSES[size],
        TONE_CLASSES[tone],
        reveal === "delayed" && "animate-in delay-300 duration-300 fade-in",
      )}
    >
      <SpinnerGraphic />
    </svg>
  );
}

const MODAL_BODY_SPINNER_INSET_CLASSES = {
  compact: "pt-space-md pb-space-sm",
  standard: "pt-space-xl pb-space-lg",
} as const;

/** Centered modal-body loading state with an established asymmetric inset. */
export function ModalBodySpinner({
  inset = "standard",
}: {
  inset?: keyof typeof MODAL_BODY_SPINNER_INSET_CLASSES;
}): ReactNode {
  return (
    <div className={twJoin("flex w-full justify-center", MODAL_BODY_SPINNER_INSET_CLASSES[inset])}>
      <Spinner size="xl" />
    </div>
  );
}
